import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { APP, AppBar } from "../../components/mobileUi";
import {
  CenterSpinner,
  EmptyText,
  FilterField,
  FilterPanel,
  ListHeaderRow,
  LoadMore,
  M,
  StatCard,
  SelectSheet,
  SummaryCard,
  useSheet,
} from "../../components/managementUi";
import {
  PAYMENT_STATUS,
  UNPAID_LOCKING_HEADERS,
  UNPAID_STATUS_OPTIONS,
  UnpaidByProjectPanel,
  UnpaidLockingCard,
  unpaidLockingRows,
} from "../../components/incomeUnpaidUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { formatVnd } from "../../utils/financeFormat";
import {
  ALL_OPTION,
  buildOptionSheetData,
  useFilterOptions,
  validId,
} from "../../utils/useFilterOptions";
import { getIncomeUnpaidListApi } from "../../api/finance.income.api";
import { EMP_BASE_PATH } from "../../config";

// Khớp app: app/(feature)/finance/income/unpaid/list.tsx
const LIMIT = 10;

const SORT_OPTIONS = [
  { key: "NEWEST", label: "Mới đến cũ", sortBy: "createdAt", sortOrder: "desc" },
  { key: "OLDEST", label: "Cũ đến mới", sortBy: "createdAt", sortOrder: "asc" },
];

const IncomeUnpaidList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const [filterOpen, setFilterOpen] = useState(false);
  const [status, setStatus] = useState("all");
  const [project, setProject] = useState(ALL_OPTION);
  const [sortKey, setSortKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  // Chỉ nạp danh mục dự án khi sheet dự án đang mở (khớp app).
  const options = useFilterOptions(sheet.type === "PROJECT" ? "PROJECT" : null, sheetSearch);

  const sortOption = SORT_OPTIONS.find((s) => s.key === sortKey) || SORT_OPTIONS[0];
  const statusLabel =
    UNPAID_STATUS_OPTIONS.find((o) => o.key === status)?.label || "Tất cả";

  const buildParams = useCallback(
    (p) => ({
      page: p,
      limit: LIMIT,
      sortBy: sortOption.sortBy,
      sortOrder: sortOption.sortOrder,
      ...(validId(project) ? { projectId: validId(project) } : {}),
      ...(status !== "all" ? { paymentStatus: status } : {}),
    }),
    [sortOption, project, status]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getIncomeUnpaidListApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setSummary(data?.summary || null);
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") {
          setItems([]);
          setSummary(null);
          setHasNext(false);
        }
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
  }, [fetchPage]);

  const amountByStatus = (s) =>
    summary?.byPaymentStatus?.find((r) => r.paymentStatus === s)?.totalUnpaid || 0;

  const filterFields = [
    <FilterField
      key="status"
      label="Trạng thái"
      value={statusLabel}
      onClick={() => sheet.open("STATUS")}
    />,
    <FilterField
      key="project"
      label="Dự án"
      value={project?.name || "Tất cả"}
      onClick={() => sheet.open("PROJECT")}
    />,
  ];

  const sheetTitle =
    { STATUS: "Trạng thái", PROJECT: "Dự án", SORT: "Sắp xếp" }[sheet.type] || "";

  const sheetData =
    sheet.type === "STATUS"
      ? UNPAID_STATUS_OPTIONS.map((o) => ({ key: o.key, label: o.label, raw: o.key }))
      : sheet.type === "SORT"
      ? SORT_OPTIONS.map((o) => ({ key: o.key, label: o.label, raw: o.key }))
      : sheet.type === "PROJECT"
      ? buildOptionSheetData(options.items, project)
      : [];

  const selectedKey = { STATUS: status, SORT: sortKey, PROJECT: project?._id }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "STATUS") setStatus(raw);
    if (sheet.type === "SORT") setSortKey(raw);
    if (sheet.type === "PROJECT") setProject(raw);
    sheet.close();
    setSheetSearch("");
  };

  const openDetail = (item) =>
    navigate(`${EMP_BASE_PATH}/tai-chinh/thu-nhap/chi-tra/${item?._id}`);

  const statCards = [
    {
      subTitle: "DỰ ÁN",
      title: "Số dự án còn tiền",
      value: summary?.totalProject ?? 0,
      icon: appImages.icon_du_an,
      bgColor: "#EDFDF5",
      textColor: "#2ABA82",
    },
    {
      subTitle: "GIAO DỊCH",
      title: "Giao dịch chưa trả hết",
      value: summary?.totalTransaction ?? 0,
      icon: appImages.icon_giao_dich,
      bgColor: "#EFF6FF",
      textColor: "#2F80ED",
    },
    {
      subTitle: "CHỜ THANH TOÁN",
      title: "Chưa phát sinh chi trả",
      value: formatVnd(amountByStatus(PAYMENT_STATUS.UNPAID)),
      icon: appImages.icon_PDV,
      bgColor: "#F5F3FF",
      textColor: "#8B5CF6",
    },
    {
      subTitle: "ĐANG THANH TOÁN",
      title: "Còn lại của đợt sau",
      value: formatVnd(amountByStatus(PAYMENT_STATUS.PARTIAL)),
      icon: appImages.icon_giai_ngan,
      bgColor: "#FFF7ED",
      textColor: "#F57127",
    },
  ];

  const selectSheet = (
    <SelectSheet
      isOpen={sheet.isOpen}
      onClose={() => {
        sheet.close();
        setSheetSearch("");
      }}
      title={sheetTitle}
      data={sheetData}
      selectedKey={selectedKey}
      onSelect={onPick}
      searchable={sheet.type === "PROJECT"}
      searchValue={sheetSearch}
      onChangeSearch={setSheetSearch}
      loading={sheet.type === "PROJECT" ? options.loading : false}
      loadingMore={sheet.type === "PROJECT" ? options.loadingMore : false}
      hasNextPage={sheet.type === "PROJECT" ? options.hasMore : false}
      onEndReached={options.loadMore}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Thu nhập chờ chi trả">
        <DesktopStatRow
          items={[
            {
              key: "summary",
              title: "TỔNG PDV CHƯA ĐƯỢC CHI TRẢ",
              total: formatVnd(summary?.totalUnpaid || 0),
              color: M.primary,
            },
            ...statCards.map((s) => ({
              key: s.subTitle,
              title: s.title,
              total: String(s.value ?? 0),
              color: s.textColor,
            })),
          ]}
        />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            {filterFields.map((el) => React.cloneElement(el, { width: "200px" }))}
            <Box w="200px">
              <Text fontSize="14px" fontWeight={500} color={M.baseContent}>
                Sắp xếp
              </Text>
              <Box h="6px" />
              <Box
                as="button"
                type="button"
                w="100%"
                h="38px"
                px="8px"
                textAlign="left"
                bg={M.field}
                borderRadius="8px"
                fontSize="14px"
                fontWeight={500}
                color={M.baseContent}
                onClick={() => sheet.open("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có giao dịch nào đang chờ chi trả"
            onRowClick={(row) => openDetail(row.raw)}
            rows={unpaidLockingRows(items)}
            headers={UNPAID_LOCKING_HEADERS}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {summary?.byProject?.length ? (
          <DesktopPanel>
            <UnpaidByProjectPanel items={summary.byProject} />
          </DesktopPanel>
        ) : null}

        {selectSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg={APP.appBg} minH="100dvh" pb="40px">
      <AppBar title="Thu nhập chờ chi trả" />

      <Box px="16px" pt="12px">
        <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)}>
          {filterFields}
        </FilterPanel>

        <Box h="12px" />
        <SummaryCard
          text="TỔNG PDV CHƯA ĐƯỢC CHI TRẢ"
          total={formatVnd(summary?.totalUnpaid || 0)}
        />

        <Box h="12px" />
        <Flex flexWrap="wrap" justify="space-between" gap="12px">
          {statCards.map((s) => (
            <StatCard key={s.subTitle} {...s} />
          ))}
        </Flex>

        <UnpaidByProjectPanel items={summary?.byProject || []} />

        <ListHeaderRow
          title="Danh sách giao dịch"
          sortLabel={sortOption.label}
          onSort={() => sheet.open("SORT")}
        />

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText text="Không có giao dịch nào đang chờ chi trả" />
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it, i) => (
              <UnpaidLockingCard
                key={it?._id || i}
                item={it}
                onClick={() => openDetail(it)}
              />
            ))}
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Flex>
        )}
      </Box>

      {selectSheet}
    </Box>
  );
};

export default IncomeUnpaidList;
